import { getDistance } from 'geolib'
import type { Point } from 'geojson'
import {
  userCoord,
  type Marker,
  type GeoPosition,
} from '@/services/map-service'
import type { SearchResultProps } from '@/services/search-service/types'
import { resizeCloudinaryImageUrl } from '@/lib/urls'
import Geographic from '@/lib/geographic'
import { GLYPH_FONTS } from '@/lib/glyph-markers-constants'
import { v4 as uuid } from 'uuid'
import { createApp } from 'vue'
import SearchResultMarker from '@/components/search/search-result-marker.vue'
import { isActive } from '@/services/feature-flag-service'

/**
 * *(AI-generated)*
 * Returns true if the search package feature flag is active.
 */
export const isSearchPackageActive = () => isActive('fox_web_search_package')

/**
 * *(AI-generated)*
 * Returns a formatted city/state string for a search result, falling back to empty string.
 */
export const getCityState = (result: SearchResultProps) => {
  if (result.nearby) {
    return `Near ${result.nearby}`
  }
  if (result.countyName && result.state?.abbreviation) {
    const countyState = `${result.countyName}, ${result.state?.abbreviation}`
    // Only show county, state if it isn't the same as the name
    // so we don't show redundant information
    if (countyState !== result.item.name) {
      return countyState
    }
  }
  return ''
}

/**
 * *(AI-generated)*
 * Returns a resized Cloudinary image URL for the given content URL.
 */
export const getResultImage = (contentUrl: string) => {
  // Resize image to 110px wide double what we display it at for retina displays
  return resizeCloudinaryImageUrl(contentUrl, 110)
}

/**
 * *(AI-generated)*
 * Returns the distance in kilometers from the user location to the given point, or null if unknown.
 */
export const getDistanceInKilometers = (point: Point) => {
  let distance = null
  const resultLocation = new Geographic(
    point.coordinates[0],
    point.coordinates[1]
  )

  const userLocation = userCoord()

  if (userLocation !== null) {
    const userCoordinates = {
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
    }

    distance = getDistance(userCoordinates, resultLocation)
  }
  if (distance) {
    return distance / 1000
  }
  return null
}

/**
 * Create a SearchResultMarker component to display on the map
 * in the location of the search result, with a glyph icon displayed that matches
 * the type of the search result.
 */
export const createSearchResultMarker = (
  resultId: string,
  resultType: string,
  location: Point,
  isSelected = false,
  markerStyle = GLYPH_FONTS.CIRCLE
): Marker => {
  // Create marker root element
  const el = document.createElement('div')
  el.className = 'search-result-marker'

  // Mount Vue component in the marker element
  const markerComponent = createApp(SearchResultMarker, {
    resultId,
    resultType,
    markerStyle,
    isSelected,
  })
  markerComponent.mount(el)

  const markerLocation: GeoPosition = [
    location.coordinates[0],
    location.coordinates[1],
  ]

  const markerFeature: Marker = {
    id: uuid(),
    location: markerLocation,
    element: el,
  }
  return markerFeature
}
